import React, {ReactNode, useId} from 'react';
import {WyscoutAnalyticsPoint} from '@/features/players/wyscoutAnalyticsApi';

export const pitchPoint = ({x, y}: WyscoutAnalyticsPoint): WyscoutAnalyticsPoint => ({
  x: Math.round(x * 100) / 100,
  y: Math.round(y * 0.68 * 100) / 100,
});

export const spatialPointLimit = 120;

export const representativePoints = <T,>(points: T[], limit = spatialPointLimit): T[] => {
  if (points.length <= limit) return points;
  return Array.from({length: limit}, (_, index) => points[Math.floor(index * (points.length - 1) / (limit - 1))]);
};

interface Props {
  title: string;
  description: string;
  children?: ReactNode;
  interactive?: boolean;
}

const Pitch: React.FC<Props> = ({title, description, children, interactive = false}) => {
  const id = useId();
  return <svg
    viewBox="0 0 100 68"
    role={interactive ? 'group' : 'img'}
    aria-labelledby={`${id}-title`}
    aria-describedby={`${id}-description`}
    className="w-full rounded-lg bg-emerald-950 shadow-inner ring-1 ring-emerald-900 dark:bg-gray-950 dark:ring-emerald-800"
  >
    <title id={`${id}-title`}>{title}</title>
    <desc id={`${id}-description`}>{description} Coordinates are team-relative, attacking from left to right.</desc>
    <g fill="none" stroke="var(--pitch-line, #d1fae5)" strokeWidth="0.45" opacity="0.8" pointerEvents="none">
      <rect x="0.5" y="0.5" width="99" height="67" rx="1" />
      <line x1="50" y1="0.5" x2="50" y2="67.5" />
      <circle cx="50" cy="34" r="9.15" />
      <circle cx="50" cy="34" r="0.65" fill="var(--pitch-line, #d1fae5)" />
      <path d="M 0.5 13.85 H 16.5 V 54.15 H 0.5 M 99.5 13.85 H 83.5 V 54.15 H 99.5" />
      <path d="M 0.5 24 H 5.5 V 44 H 0.5 M 99.5 24 H 94.5 V 44 H 99.5" />
      <circle cx="11" cy="34" r="0.55" fill="var(--pitch-line, #d1fae5)" />
      <circle cx="89" cy="34" r="0.55" fill="var(--pitch-line, #d1fae5)" />
      <path d="M 12 0.5 A 11.5 11.5 0 0 1 0.5 12 M 88 0.5 A 11.5 11.5 0 0 0 99.5 12 M 12 67.5 A 11.5 11.5 0 0 0 0.5 56 M 88 67.5 A 11.5 11.5 0 0 1 99.5 56" />
    </g>
    <g aria-hidden="true" className="fill-emerald-100 text-[2.4px] font-semibold dark:fill-emerald-200">
      <text x="3" y="4">DEFEND</text>
      <text x="85" y="4">ATTACK &gt;</text>
    </g>
    {children}
  </svg>;
};

export default Pitch;
